/**
 * Booking-link tracking + page views (server side: inc/tracking.php).
 *
 * Per browser tab (sessionStorage, so it survives page loads but nothing is
 * baked into cached HTML):
 *   sessionId     random UUID, created on the first page view
 *   referrer      the first external referrer of the visit
 *   trackingCode  the ?od= campaign code of the landing URL (latest wins)
 *   gclid         Google Ads click id (?gclid=), when present
 *
 * Booking links (booking.dentalmedia.io) get sessionId, trackingCode and
 * gclid, plus referrer = the URL of the page the link was clicked on (same
 * parameter names as the old theme)
 * the moment they're clicked, so links rendered later by JS (clinic finder)
 * are covered too. Existing parameters such as ?clinicId= are kept.
 *
 * Page views are only sent when window.odontiaTracking.pageViews is true
 * (needs DIGGER_API_URL + ODONTIA_TRACK_PAGE_VIEWS, see odontia-config.php).
 */

const STORE_KEY = 'odontiaTracking';
const BOOKING_HOSTS = [ 'dentalmedia.io' ];

function readState() {
	try {
		return JSON.parse( sessionStorage.getItem( STORE_KEY ) ) || {};
	} catch {
		return {};
	}
}

function writeState( state ) {
	try {
		sessionStorage.setItem( STORE_KEY, JSON.stringify( state ) );
	} catch {
		// storage blocked: tracking just stays per page
	}
}

function uuid() {
	if ( window.crypto?.randomUUID ) {
		return window.crypto.randomUUID();
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace( /[xy]/g, ( c ) => {
		const r = ( Math.random() * 16 ) | 0;
		return ( c === 'x' ? r : ( r & 0x3 ) | 0x8 ).toString( 16 );
	} );
}

function initState() {
	const state = readState();
	const params = new URLSearchParams( window.location.search );

	if ( ! state.sessionId ) {
		state.sessionId = uuid();
	}

	if ( ! state.referrer && document.referrer ) {
		try {
			if ( new URL( document.referrer ).host !== window.location.host ) {
				state.referrer = document.referrer;
			}
		} catch {
			// invalid referrer — ignore
		}
	}

	if ( params.get( 'od' ) ) {
		state.trackingCode = params.get( 'od' );
	}

	if ( params.get( 'gclid' ) ) {
		state.gclid = params.get( 'gclid' );
	}

	writeState( state );

	return state;
}

const state = initState();

function isBookingLink( anchor ) {
	try {
		const url = new URL( anchor.href );
		return BOOKING_HOSTS.some( ( host ) => url.hostname === host || url.hostname.endsWith( '.' + host ) );
	} catch {
		return false;
	}
}

function decorate( anchor ) {
	if ( ! isBookingLink( anchor ) ) {
		return;
	}

	const url = new URL( anchor.href );
	const values = {
		sessionId: state.sessionId,
		// The page the booking link was clicked on (without its own query).
		referrer: window.location.origin + window.location.pathname,
		trackingCode: state.trackingCode || '',
		gclid: state.gclid || '',
	};

	Object.entries( values ).forEach( ( [ key, value ] ) => url.searchParams.set( key, value ) );
	anchor.href = url.toString();
}

// Just before following (left / middle click, keyboard, "open in new tab").
[ 'click', 'auxclick', 'contextmenu' ].forEach( ( type ) => {
	document.addEventListener( type, ( event ) => {
		const anchor = event.target.closest?.( 'a[href]' );
		if ( anchor ) {
			decorate( anchor );
		}
	}, true );
} );

// Page view (off until the new tracking API is configured).
const config = window.odontiaTracking || {};

if ( config.pageViews && config.ajaxUrl ) {
	const body = new URLSearchParams( {
		action: 'track_page_view',
		sessionId: state.sessionId,
		trackingCode: state.trackingCode || '',
		referrer: state.referrer || '',
		path: window.location.pathname + window.location.search,
	} );

	if ( navigator.sendBeacon ) {
		navigator.sendBeacon( config.ajaxUrl, body );
	} else {
		fetch( config.ajaxUrl, { method: 'POST', body, credentials: 'same-origin', keepalive: true } ).catch( () => {} );
	}
}
